import { supabase, TABLES, COLUMN_MAPS } from '../supabase';
import { TimelineEvent } from '../../types';
import { PAGINATION } from '../../utils/constants';

// ============================================
// Timeline Service
// ============================================

export const timelineApi = {
  /**
   * Get all timeline events
   */
  async getAll(): Promise<TimelineEvent[]> {
    const { data, error } = await supabase
      .from(TABLES.TIMELINE)
      .select('*')
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.timeline.fromDb);
  },

  /**
   * Get timeline events with pagination
   */
  async getPaginated(
    page = PAGINATION.DEFAULT_PAGE,
    limit = PAGINATION.DEFAULT_LIMIT
  ): Promise<{ data: TimelineEvent[]; total: number }> {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.TIMELINE)
      .select('*', { count: 'exact' })
      .order('timestamp', { ascending: false })
      .range(from, to);
    
    if (error) throw error;
    
    return {
      data: (data || []).map(COLUMN_MAPS.timeline.fromDb),
      total: count || 0,
    };
  },

  /**
   * Get timeline events for a project
   */
  async getByProject(projectId: string): Promise<TimelineEvent[]> {
    const { data, error } = await supabase
      .from(TABLES.TIMELINE)
      .select('*')
      .eq('project_id', projectId)
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.timeline.fromDb);
  },

  /**
   * Get timeline events by user
   */
  async getByUser(userId: string): Promise<TimelineEvent[]> {
    const { data, error } = await supabase
      .from(TABLES.TIMELINE)
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.timeline.fromDb);
  },

  /**
   * Get recent timeline events (last N)
   */
  async getRecent(limit = 20): Promise<TimelineEvent[]> {
    const { data, error } = await supabase
      .from(TABLES.TIMELINE)
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.timeline.fromDb);
  },

  /**
   * Create a timeline event
   */
  async create(event: TimelineEvent): Promise<void> {
    const dbData = COLUMN_MAPS.timeline.toDb(event);
    
    const { error } = await supabase
      .from(TABLES.TIMELINE)
      .insert(dbData);
    
    if (error) {
      console.error('Timeline insertion failed:', error);
      throw error;
    }
  },

  /**
   * Delete timeline events for a project
   */
  async deleteByProject(projectId: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.TIMELINE)
      .delete()
      .eq('project_id', projectId);
    
    if (error) throw error;
  },
};

export default timelineApi;
